import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Button, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Store } from 'lucide-react';
import { brandVars } from '@/theme/applyTheme';
import { SectionCard, QueryState } from '@/components';
import { errorText } from '@/lib/format';
import Can from '@rbac/Can';
import { useCrudList, useCrudWrite } from '../../hooks';
import { BRAND_KEYS, brandingChanges, brandingPayload } from '../../lib/settings';


/** SELL-08 Branding: store name and tagline (Setting rows). Colours, logo and the landing page live on Appearance (D24). */
export default function BrandingTab({ store }) {
  const { toast } = useToast();
  const settings = useCrudList('Setting', { where: { key: BRAND_KEYS }, limit: 10 });
  const write = useCrudWrite();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (settings.data) setForm(Object.fromEntries(BRAND_KEYS.map((k) => [k, ((settings.data.find((r) => r.key === k) || {}).value) || ''])));
  }, [settings.data]);

  const changes = form ? brandingChanges(settings.data, form) : [];
  const save = async () => {
    setError('');
    if (!String(form['brand.name']).trim()) {
      setError('Store name is required');
      return;
    }
    try {
      // One audited action (store.branding_save); generic Setting writes refuse brand.* keys.
      await write.run({ model: 'Setting', action: 'saveBranding', data: brandingPayload(changes) });
      toast.success('Branding saved', 'Your storefront picks it up on the next visit.');
      settings.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  return (
    <QueryState query={settings}>
      {() =>
        form ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard title="Brand">
              <Input label="Store name" required value={form['brand.name']} onChange={(e) => setForm({ ...form, 'brand.name': e.target.value })} helperText={store && store.name !== form['brand.name'] ? `Registered business name: ${store.name}` : undefined} />
              <Input label="Tagline" value={form['brand.tagline']} onChange={(e) => setForm({ ...form, 'brand.tagline': e.target.value })} maxLength={120} showCount />
              <Alert variant="info" title="Colours, logo and the landing page moved">
                Set them on the <Link to="/appearance" className="font-medium underline">Appearance</Link> page, with a live preview and publishing.
              </Alert>
              {error ? <Alert variant="error">{error}</Alert> : null}
              <Can module="core" action="update" fallback={<Alert variant="info">You can view branding; changing it needs the settings update permission.</Alert>}>
                <div className="flex gap-2">
                  <Button onClick={save} loading={write.loading} disabled={!changes.length}>Save branding</Button>
                  {changes.length ? <Button variant="ghost" onClick={() => settings.reload()}>Discard</Button> : null}
                </div>
              </Can>
            </SectionCard>
            <SectionCard title="Preview">
              {/* The chosen colour is applied as the token variable, scoped to this preview only. */}
              <div className="rounded-lg border border-[var(--app-border)] overflow-hidden" style={form['brand.primary'] ? brandVars({ '--color-primary-600': form['brand.primary'] }) : undefined}>
                <div className="bg-primary-600 text-white p-4 flex items-center gap-2">
                  <Store size={20} aria-hidden />
                  <span className="font-bold text-lg truncate">{form['brand.name'] || 'Store name'}</span>
                </div>
                <div className="p-4 space-y-3 bg-[var(--app-surface)]">
                  <p className="text-sm opacity-80">{form['brand.tagline'] || 'Your tagline appears here.'}</p>
                  <span className="inline-flex items-center rounded-md bg-primary-600 text-white px-4 py-2 text-sm font-medium">Shop now</span>
                </div>
              </div>
            </SectionCard>
          </div>
        ) : null
      }
    </QueryState>
  );
}
